import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FiRefreshCw, FiGrid, FiFolder, FiUsers, FiList } from "react-icons/fi";
import api from "../../services/api";
import { Page, TopBar, LiveDot, IconButton } from "./styles";
import { SegmentedControl } from "./components/primitives";
import Overview from "./components/Overview";
import EntityList from "./components/EntityList";
import Timeline from "./components/Timeline";
import DetailSheet from "./components/DetailSheet";
import { PERIODS, periodRange, relativeTime } from "./labels";

const PAGE_SIZE = 60;
const REFRESH_MS = 60000;
const EMPTY_FILTERS = { type: "", module: "", status: "", userId: "", projectId: "" };

export default function ActivityLogs() {
  const [tab, setTab] = useState("overview");
  const [period, setPeriod] = useState("7d");
  const [scrolled, setScrolled] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [, forceTick] = useState(0);

  // Visão geral + listas
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingBase, setLoadingBase] = useState(true);

  // Linha do tempo
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [modules, setModules] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Folha lateral (pilha de navegação)
  const [stack, setStack] = useState([]);

  const sentinel = useRef(null);
  const baseParams = useMemo(() => periodRange(period), [period]);

  // Borda da barra superior aparece ao rolar (como a navigation bar do iOS)
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setScrolled(!e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Atualiza o rótulo "atualizado há..." a cada 30s
  useEffect(() => {
    const t = setInterval(() => forceTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, []);

  const loadBase = useCallback(async (silent = false) => {
    if (!silent) setLoadingBase(true);
    try {
      const [s, u, p] = await Promise.all([
        api.get("/activity-logs/summary", { params: baseParams }),
        api.get("/activity-logs/entities", { params: { ...baseParams, by: "user" } }),
        api.get("/activity-logs/entities", { params: { ...baseParams, by: "project" } }),
      ]);
      setSummary(s.data.summary);
      setUsers(u.data.items || []);
      setProjects(p.data.items || []);
      setUpdatedAt(new Date().toISOString());
    } catch {
      if (!silent) toast.error("Erro ao carregar atividades");
    } finally {
      if (!silent) setLoadingBase(false);
    }
  }, [baseParams]);

  const eventParams = useMemo(() => {
    const p = { ...baseParams, q: debounced || undefined };
    Object.entries(filters).forEach(([k, v]) => { if (v) p[k] = v; });
    return p;
  }, [baseParams, filters, debounced]);

  const loadEvents = useCallback(async (silent = false) => {
    if (!silent) setLoadingEvents(true);
    try {
      const r = await api.get("/activity-logs", { params: { ...eventParams, size: PAGE_SIZE, from: 0 } });
      setEvents(r.data.items || []);
      setEventsTotal(r.data.total || 0);
    } catch {
      if (!silent) toast.error("Erro ao carregar a linha do tempo");
    } finally {
      if (!silent) setLoadingEvents(false);
    }
  }, [eventParams]);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { if (tab === "timeline") loadEvents(); }, [tab, loadEvents]);

  useEffect(() => {
    api.get("/activity-logs/filters").then((r) => setModules(r.data.modules || [])).catch(() => {});
  }, []);

  // Atualização automática silenciosa (pausa com a folha aberta para não "pular" conteúdo)
  useEffect(() => {
    if (stack.length > 0) return undefined;
    const t = setInterval(() => {
      loadBase(true);
      if (tab === "timeline" && events.length <= PAGE_SIZE) loadEvents(true);
    }, REFRESH_MS);
    return () => clearInterval(t);
  }, [loadBase, loadEvents, tab, stack.length, events.length]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await api.get("/activity-logs", { params: { ...eventParams, size: PAGE_SIZE, from: events.length } });
      setEvents((prev) => [...prev, ...(r.data.items || [])]);
    } catch {
      toast.error("Erro ao carregar mais eventos");
    } finally {
      setLoadingMore(false);
    }
  };

  const refresh = () => {
    loadBase();
    if (tab === "timeline") loadEvents();
  };

  // Índices para enriquecer avatares/nomes em qualquer lugar da tela
  const lookup = useMemo(() => ({
    users: Object.fromEntries(users.map((u) => [u.id, u])),
    projects: Object.fromEntries(projects.map((p) => [p.id, p])),
  }), [users, projects]);

  const open = (kind, item) => setStack([{ kind, item }]);
  const goTab = (t, extraFilters) => {
    if (extraFilters) setFilters({ ...EMPTY_FILTERS, ...extraFilters });
    setTab(t);
  };

  const todayLabel = new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <>
    <Page>
      <div ref={sentinel} style={{ height: 1 }} />
      <TopBar $scrolled={scrolled}>
        <div className="title-row">
          <div>
            <div className="eyebrow">Vidal · Registro de atividades</div>
            <h1>Atividades</h1>
            <div className="meta">
              <LiveDot>Ao vivo</LiveDot>
              <span className="update-tag" style={{ textTransform: "capitalize" }}>{todayLabel}</span>
              {updatedAt && <span className="update-tag">atualizado {relativeTime(updatedAt)}</span>}
            </div>
          </div>
          <div className="actions">
            <IconButton onClick={refresh} title="Atualizar" aria-label="Atualizar">
              <FiRefreshCw className={loadingBase ? "spin" : ""} />
            </IconButton>
          </div>
        </div>

        <div className="nav-row">
          <SegmentedControl
            value={tab}
            onChange={setTab}
            options={[
              { key: "overview", icon: <FiGrid />, label: "Visão geral" },
              { key: "projects", icon: <FiFolder />, label: "Projetos", count: loadingBase ? undefined : projects.length },
              { key: "users", icon: <FiUsers />, label: "Usuários", count: loadingBase ? undefined : users.length },
              { key: "timeline", icon: <FiList />, label: "Linha do tempo" },
            ]}
          />
          <SegmentedControl
            small
            value={period}
            onChange={setPeriod}
            options={PERIODS.map((p) => ({ key: p.key, label: p.label }))}
          />
        </div>
      </TopBar>

      {tab === "overview" && (
        <Overview
          summary={summary}
          users={users}
          projects={projects}
          loading={loadingBase}
          period={period}
          onOpen={open}
          onGoTab={goTab}
        />
      )}

      {tab === "projects" && (
        <EntityList kind="project" items={projects} loading={loadingBase} onOpen={open} />
      )}

      {tab === "users" && (
        <EntityList kind="user" items={users} loading={loadingBase} onOpen={open} />
      )}

      {tab === "timeline" && (
        <Timeline
          items={events}
          total={eventsTotal}
          loading={loadingEvents}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          onSelect={(e) => open("event", e)}
          filters={filters}
          setFilters={setFilters}
          search={search}
          setSearch={setSearch}
          modules={modules}
          lookup={lookup}
        />
      )}
    </Page>

      {stack.length > 0 && (
        <DetailSheet
          stack={stack}
          onPush={(p) => setStack((s) => [...s, p])}
          onPop={() => setStack((s) => s.slice(0, -1))}
          onClose={() => setStack([])}
          baseParams={baseParams}
          lookup={lookup}
        />
      )}
    </>
  );
}
